// @ts-nocheck
/**
 * Voterra Campaign Command Center — main dashboard shell.
 *
 * Session 5.11: Navigation overhaul. The old 19-button wall at the bottom of
 * MapView is replaced by:
 *   1. AppShell — persistent top bar with hamburger menu trigger + breadcrumbs
 *   2. HamburgerMenu — slide-out categorized menu, RBAC-filtered per role
 *   3. Breadcrumbs — clickable trail with real back capability
 *   4. Contextual links — smart quick-jump pills below breadcrumbs
 *
 * The geographic drill chain (Map → LD → Subregion → Precinct) now builds a
 * proper breadcrumb trail so back navigation follows the chain instead of
 * always teleporting to Map.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import type { ViewType, DrillTarget } from '@/types';
import { MapView } from '@/components/views/MapView';
import { HomeView, LAST_VIEW_STORAGE_KEY, LAST_VIEW_EXCLUDED } from '@/components/views/HomeView';
import { LDView } from '@/components/views/LDView';
import { SubregionView } from '@/components/views/SubregionView';
import { PrecinctView } from '@/components/views/PrecinctView';
import { RoutesView } from '@/components/views/RoutesView';
import { ScriptsView } from '@/components/views/ScriptsView';
import { TurnoutHeatmapView } from '@/components/views/TurnoutHeatmapView';
import { DropOffView } from '@/components/views/DropOffView';
import { CanvassView } from '@/components/views/CanvassView';
import { CanvassReviewView } from '@/components/views/CanvassReviewView';
import { CampaignIssuesView } from '@/components/views/CampaignIssuesView';
import { CampaignTalkingPointsView } from '@/components/views/CampaignTalkingPointsView';
import { GlossaryView } from '@/components/views/GlossaryView';
import OptimizerView from '@/components/views/OptimizerView';
import { EngagementHeatmapView } from '@/components/views/EngagementHeatmapView';
import { VotersView } from '@/components/views/VotersView';
import { RoutesListView } from '@/components/views/RoutesListView';
import { ActivityView } from '@/components/views/ActivityView';
import { CoverageView } from '@/components/views/CoverageView';
import { PowerMapView } from '@/components/views/PowerMapView';
import { RLeanView } from '@/components/views/RLeanView';
import { TurnoutAccuracyView } from '@/components/views/TurnoutAccuracyView';
import { FeedbackView } from '@/components/views/FeedbackView';
import { DataChangesView } from '@/components/views/DataChangesView';
import { LeadsView } from '@/components/views/LeadsView';
import { MyVolunteersView } from '@/components/views/MyVolunteersView';
import { MyResponsesView } from '@/components/views/MyResponsesView';
import { MyProfileView } from '@/components/views/MyProfileView';
import { MailListBuilderView } from '@/components/views/MailListBuilderView';
import { ComplianceView } from '@/components/views/ComplianceView';
import { ChannelPlannerView } from '@/components/views/ChannelPlannerView';
import { EnrichmentReconcileView } from '@/components/views/EnrichmentReconcileView';
import { SettingsView } from '@/components/views/SettingsView';
import { UsersAdminView } from '@/components/admin/UsersAdminView';
import { CandidatesAdminView } from '@/components/admin/CandidatesAdminView';
import { CampaignSetupView } from '@/components/admin/CampaignSetupView';
// SMS messaging views (Track B, 2026-05-17)
import { MessengerQueueView } from '@/components/views/MessengerQueueView';
import { CampaignsView } from '@/components/views/CampaignsView';
import { CampaignComposerView } from '@/components/views/CampaignComposerView';
import { CampaignDetailView } from '@/components/views/CampaignDetailView';
import { MessagingNumbersView } from '@/components/views/MessagingNumbersView';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { LiveBackendStatus } from '@/components/LiveBackendStatus';
import { RealAuthGate } from '@/components/RealAuthGate';
import { AcceptInvite } from '@/components/AcceptInvite';
import { AppShell, BottomNav, getContextualLinks, getVisibleItems, getBottomNavItems, type BreadcrumbItem } from '@/components/navigation';
import { homeViewForRole } from '@/components/navigation/NavConfig';
import { useAuth } from '@/context/AuthContext';
import { useMapRegions } from '@/context/MapRegionsContext';
import { regionForPrecinct } from '@/api/geoRegions';
import { AUTH_UNAUTHORIZED_EVENT } from '@/api/client';

// -------------------------------------------------------------------------
// View label map — used by breadcrumbs + contextual links to show
// human-readable names for each view key.
// -------------------------------------------------------------------------
const VIEW_LABELS: Record<string, string> = {
  home: 'Home',
  map: 'Map',
  ld: 'Legislative District',
  subregion: 'Subregion',
  precinct: 'Precinct',
  voters: 'Voters',
  routes: 'Route Viewer',
  'routes-list': 'Routes List',
  activity: 'Activity',
  coverage: 'Coverage',
  'power-map': 'Power Map',
  scripts: 'Door Scripts',
  heatmap: 'Turnout Heatmap',
  dropoff: 'Drop-Off Detector',
  canvass: 'Canvass Shift',
  'canvass-review': 'AAR Inbox',
  'campaign-issues': 'Campaign Issues',
  'talking-points': 'Talking Points',
  glossary: 'Glossary',
  optimizer: 'Resource Optimizer',
  engagement: 'Engagement Heatmap',
  'r-lean': 'R-Lean Targeting',
  'turnout-accuracy': 'Model Accuracy',
  feedback: 'Feedback',
  'data-changes': 'Data Changes',
  leads: 'Access Requests',
  'my-staff': 'My Volunteers',
  'my-responses': 'My Responses',
  'my-profile': 'My Profile',
  'mail-builder': 'Mail List Builder',
  'compliance': 'Compliance',
  'channel-planner': 'Channel Planner',
  'enrichment-reconcile': 'Enrichment Reconcile',
  users: 'Users',
  candidates: 'Candidates',
  settings: 'Settings',
  // SMS messaging (Track B)
  'messenger-queue': 'Send Texts',
  campaigns: 'Campaigns',
  'campaign-composer': 'New Campaign',
  'campaign-detail': 'Campaign Detail',
  'messaging-numbers': '10DLC Numbers',
};

export default function Dashboard() {
  const [view, setView] = useState("map");
  const [selectedLD, setSelectedLD] = useState(null);
  const [selectedSR, setSelectedSR] = useState(null);
  const [selectedPrecinct, setSelectedPrecinct] = useState(null);
  const [bootReady, setBootReady] = useState(!!localStorage.getItem('access_token'));
  const [glossaryTermId, setGlossaryTermId] = useState<string | null>(null);
  // Track B (SMS): campaign-detail needs a campaignId. Parent state so we
  // can route into it from CampaignsView via handleDrill('campaign-detail',
  // {campaignId}) and also let CampaignComposerView jump straight to the
  // detail of the campaign it just created.
  const [campaignDetailId, setCampaignDetailId] = useState<number | null>(null);
  // Route-focus request: set when jumping to a walking-route stop from a
  // VoterDetailDrawer address click. SubregionView consumes it (selects the
  // shift + focuses the stop), then clears it via onRouteFocusConsumed.
  const [routeFocus, setRouteFocus] = useState<{ shiftId: string; stopIndex: number } | null>(null);
  const { user, refreshUser, logout } = useAuth();

  // Where "home" is for THIS user. Everyone lands on the county map except a
  // canvasser, whose home is the doors they were given — the map's stats 403
  // for them and a turnout heatmap is not what a door knocker opens the app
  // for. Every place below that used to hardcode 'map' to mean "home" uses
  // this; for every other role it still resolves to 'map', so behaviour is
  // unchanged for them.
  const homeView = homeViewForRole(user?.role);

  // `view` initialises to 'map' before /auth/me has resolved, so a canvasser
  // would flash the county map for one render. When the role lands (or
  // CHANGES — logging out and back in as another account swaps roles without
  // remounting this component), move off the two "default" views only:
  // someone who deliberately navigated somewhere must not be yanked home.
  // Both defaults need handling — 'map' is the pre-auth initial state, and
  // 'home' is where the previous role's session may have been sitting when
  // the view lands on a canvasser, whose menu has no 'home' at all.
  useEffect(() => {
    if (!user?.role) return;
    setView((current) =>
      (current === 'map' || current === 'home') && current !== homeView ? homeView : current,
    );
  }, [user?.role, homeView]);
  // This tenant's turfs. Empty (never Platte) while the fetch is in flight, so
  // every read below tolerates a miss — a breadcrumb falls back to the turf
  // code, and a drill that cannot resolve an LD simply leaves it unset rather
  // than labelling the crumb with another county's district.
  const { catalog: regions, refresh: refreshRegions } = useMapRegions();

  // Global listener for InfoTip "Learn more →" links and OnboardingModal
  // "Open full glossary →" buttons. Both fire the same custom event with
  // an optional term id. We switch to the glossary view and forward the
  // id so GlossaryView can scroll-to + highlight the matching entry.
  // NOTE: must be declared before any conditional return to satisfy
  // React's rules of hooks.
  useEffect(() => {
    function handler(e: any) {
      const id = e?.detail?.termId ?? null;
      setGlossaryTermId(id);
      setView('glossary');
    }
    window.addEventListener('infotip:navigate', handler);
    return () => window.removeEventListener('infotip:navigate', handler);
  }, []);

  // -----------------------------------------------------------------------
  // Breadcrumb trail — computed from current view + geo drill state.
  // The geographic drill chain (Map → LD → Subregion → Precinct) builds a
  // proper trail. All other views get a simple [<home>, <View>] trail.
  //
  // The root is the ROLE's home, not always the map: a canvasser has no map,
  // so rooting their trail at one would render a crumb that 403s when tapped.
  // -----------------------------------------------------------------------
  const breadcrumbTrail = useMemo<BreadcrumbItem[]>(() => {
    if (view === homeView) return [];

    const trail: BreadcrumbItem[] = [
      homeView === 'canvass'
        ? { key: 'canvass', label: 'My doors' }
        : { key: 'home', label: 'Home' },
    ];

    // Geographic drill chain — rooted at the Map, which sits under Home now
    // that the map is no longer itself the home view.
    const isGeoDrill =
      (view === 'ld' && selectedLD) ||
      (view === 'subregion' && selectedSR) ||
      (view === 'precinct' && selectedPrecinct !== null);
    if (isGeoDrill || view === 'map') {
      trail.push({ key: 'map', label: 'Map' });
    }

    if (view === 'ld' && selectedLD) {
      trail.push({ key: 'ld', label: `LD ${selectedLD}` });
    } else if (view === 'subregion' && selectedSR) {
      if (selectedLD) trail.push({ key: 'ld', label: `LD ${selectedLD}` });
      const srName = regions.byKey[selectedSR]?.name || selectedSR;
      trail.push({ key: 'subregion', label: srName });
    } else if (view === 'precinct' && selectedPrecinct !== null) {
      if (selectedLD) trail.push({ key: 'ld', label: `LD ${selectedLD}` });
      if (selectedSR) {
        const srName = regions.byKey[selectedSR]?.name || selectedSR;
        trail.push({ key: 'subregion', label: srName });
      }
      trail.push({ key: 'precinct', label: `Precinct ${selectedPrecinct}` });
    } else if (view !== 'map') {
      // Non-geographic view — simple flat trail
      trail.push({ key: view, label: VIEW_LABELS[view] || view });
    }

    return trail;
  }, [view, selectedLD, selectedSR, selectedPrecinct, regions, homeView]);


  // Contextual links for the current view, role-filtered so we don't surface
  // links to views the user can't access (e.g. canvasser shouldn't see Optimizer
  // pills on the map). Falls back to unfiltered when no role is known.
  const contextualLinks = useMemo(() => {
    const links = getContextualLinks(view);
    if (!user?.role) return links;
    const visibleKeys = new Set(getVisibleItems(user.role).map(i => i.key));
    return links.filter(link => visibleKeys.has(link.key));
  }, [view, user?.role]);

  // -----------------------------------------------------------------------
  // Browser history management — push/replace state on navigation
  // -----------------------------------------------------------------------
  const isFirstRender = useRef(true);
  // Set true right before a back/forward press drives our view state, so the
  // sync effect below knows this change came FROM history and must not push a
  // new entry. Without this guard, every Back press re-pushes the popped view,
  // wiping the forward stack and requiring multiple presses to move.
  const isPopStateNav = useRef(false);
  useEffect(() => {
    if (isFirstRender.current) {
      // Replace (don't push) the initial state so popstate has something to land on
      const state = { view, selectedLD, selectedSR, selectedPrecinct };
      window.history.replaceState(state, '', `#${view}`);
      isFirstRender.current = false;
      return;
    }
    if (isPopStateNav.current) {
      // This change was triggered by the browser back/forward button — the URL
      // and history position already reflect it, so consume the flag and skip
      // the push (pushing here would clobber the forward stack).
      isPopStateNav.current = false;
      return;
    }
    const state = { view, selectedLD, selectedSR, selectedPrecinct };
    window.history.pushState(state, '', `#${view}`);
  }, [view, selectedLD, selectedSR, selectedPrecinct]);

  // -----------------------------------------------------------------------
  // Listen for browser back/forward button presses
  // -----------------------------------------------------------------------
  useEffect(() => {
    const handlePopState = (e: PopStateEvent) => {
      // Mark this as history-driven so the sync effect doesn't re-push it.
      isPopStateNav.current = true;
      if (e.state) {
        setView(e.state.view || homeView);
        setSelectedLD(e.state.selectedLD || null);
        setSelectedSR(e.state.selectedSR || null);
        setSelectedPrecinct(e.state.selectedPrecinct ?? null);
      } else {
        // No state = initial entry, go to this role's home
        setView(homeView);
        setSelectedLD(null);
        setSelectedSR(null);
        setSelectedPrecinct(null);
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [homeView]);

  // -----------------------------------------------------------------------
  // Session-lost → return to the login/home gate.
  // The axios interceptor drops the token and fires this event on any 401
  // (invalid stored token at boot, or a mid-session expiry). We clear auth
  // state and flip `bootReady` off so the RealAuthGate renders instead of a
  // broken, data-less dashboard. Resetting the view to
  // the role's home means re-login lands there rather than the screen that failed.
  // -----------------------------------------------------------------------
  useEffect(() => {
    const handleUnauthorized = () => {
      logout();
      setBootReady(false);
      setView(homeView);
      setSelectedLD(null);
      setSelectedSR(null);
      setSelectedPrecinct(null);
    };
    window.addEventListener(AUTH_UNAUTHORIZED_EVENT, handleUnauthorized);
    return () =>
      window.removeEventListener(AUTH_UNAUTHORIZED_EVENT, handleUnauthorized);
  }, [logout, homeView]);

  // If no token, show the login gate instead of the dashboard. The login
  // flow writes the token to localStorage, so we MUST call refreshUser() to
  // load /auth/me for the newly-authenticated account — otherwise
  // AuthContext.user stays null and every role-gated nav/view falls back to
  // 'viewer'.
  if (!bootReady) {
    const handleAuthSuccess = () => {
      refreshUser();
      // The turf fetch at provider mount found no token (this gate is what
      // renders when there isn't one), so it has to be kicked again now that
      // one exists — same reason refreshUser() is called here.
      void refreshRegions();
      setBootReady(true);
    };

    // An invited person has no account to log into yet, so the invite screen
    // has to come BEFORE the auth gate. Query param rather than a path
    // because the app has no router and the app host has no SPA fallback
    // rewrite — see `inviteUrl` in lib/inviteLinks.
    const inviteToken = new URLSearchParams(window.location.search).get('invite');
    if (inviteToken) {
      return <AcceptInvite token={inviteToken} onSuccess={handleAuthSuccess} />;
    }

    return <RealAuthGate onSuccess={handleAuthSuccess} />;
  }

  // -----------------------------------------------------------------------
  // Unified navigation handler — dispatches to appropriate handler based on
  // target view. Geographic drill (map, ld, subregion, precinct) handlers
  // have special state management; all others just set the view.
  // -----------------------------------------------------------------------
  const handleDrill = (target, value) => {
    // Remember the last parameterless view the user DELIBERATELY opened, so
    // Home can offer "pick up where you left off". Recorded here, not in an
    // effect on `view`: the boot sequence passes through 'map' before the
    // role's home redirect lands, and an effect would record that flash as a
    // visit. Views that need drill state (an LD, a campaign id) are excluded —
    // a cold link into them renders nothing.
    if (!LAST_VIEW_EXCLUDED.has(target) && target !== homeView) {
      try {
        localStorage.setItem(
          LAST_VIEW_STORAGE_KEY,
          JSON.stringify({ key: target, label: VIEW_LABELS[target] || target }),
        );
      } catch {
        // Storage full/blocked — the chip just won't render. Not worth surfacing.
      }
    }
    switch (target) {
      case 'map':
        setView('map');
        setSelectedLD(null);
        setSelectedSR(null);
        setSelectedPrecinct(null);
        return;
      case 'ld':
        setView('ld');
        setSelectedLD(value);
        setSelectedSR(null);
        setSelectedPrecinct(null);
        return;
      case 'subregion':
        setView('subregion');
        setSelectedSR(value);
        setSelectedLD(regions.byKey[value]?.ld);
        setSelectedPrecinct(null);
        return;
      case 'precinct': {
        setView('precinct');
        setSelectedPrecinct(value);
        const region = regionForPrecinct(regions, value);
        if (region) {
          setSelectedSR(region.key);
          setSelectedLD(region.ld);
        }
        return;
      }
      case 'glossary':
        setGlossaryTermId(null);
        setView('glossary');
        return;
      case 'campaign-detail': {
        // Carry the campaignId param. CampaignsView / CampaignComposerView
        // pass `{campaignId}` as the second arg via onNavigate.
        const campaignId =
          value && typeof value === 'object' && 'campaignId' in value
            ? (value as { campaignId: number }).campaignId
            : typeof value === 'number'
              ? value
              : null;
        if (campaignId != null) {
          setCampaignDetailId(campaignId);
        }
        setView('campaign-detail');
        return;
      }
      default:
        // All non-geographic views: just set the view string
        setView(target);
    }
  };

  // Jump to a specific walking-route stop from a VoterDetailDrawer address
  // click. Mirrors handleDrill's 'subregion' case exactly, then records the
  // route-focus request so SubregionView can select the shift + stop.
  const handleJumpToRoute = (subregion: string, shiftId: string, stopIndex: number) => {
    setView('subregion');
    setSelectedSR(subregion);
    setSelectedLD(regions.byKey[subregion]?.ld);
    setSelectedPrecinct(null);
    setRouteFocus({ shiftId, stopIndex });
  };

  return (
    <AppShell
      currentView={view}
      breadcrumbTrail={breadcrumbTrail}
      onNavigate={(viewKey) => handleDrill(viewKey)}
      contextualLinks={contextualLinks}
      homeKey={homeView}
      bottomNav={(openMenu) =>
        user?.role ? (
          <BottomNav
            homeKey={homeView}
            items={getBottomNavItems(user.role)}
            currentView={view}
            onNavigate={(viewKey) => handleDrill(viewKey)}
            onOpenMenu={openMenu}
          />
        ) : null
      }
      headerRight={
        <div className="flex items-center gap-2">
          <span className="bg-bg-raised text-fg-muted text-xs font-semibold px-2 py-1 rounded-full max-w-[40vw] sm:max-w-none truncate">
            {user?.email || user?.full_name || 'Signed in'}
          </span>
          <button
            onClick={() => {
              logout();
              setBootReady(false);
            }}
            className="bg-accent hover:bg-accent-hover text-white text-xs font-semibold px-2 py-1 rounded-full transition-colors"
            title="Log out"
          >
            Log out
          </button>
        </div>
      }
    >
      {view === "map" && <LiveBackendStatus />}
      {/* Phase 4 scope reflection — a scoped user sees why their data is limited.
          The backend is the security boundary; this banner is informational. */}
      {(user?.scopes?.length ?? 0) > 0 && (
        <div className="mx-4 mt-2 rounded-md border border-accent/30 bg-accent/10 px-3 py-1.5 text-xs font-medium text-accent">
          Scoped: {user.scopes.map((s) => s.label).join('  •  ')}
        </div>
      )}
      {view === "home" && <ErrorBoundary label="HomeView"><HomeView onNavigate={handleDrill} /></ErrorBoundary>}
      {view === "map" && <ErrorBoundary label="MapView"><MapView onDrill={handleDrill} /></ErrorBoundary>}
      {view === "ld" && selectedLD && <ErrorBoundary label="LDView"><LDView ld={selectedLD} onDrill={handleDrill} onBack={() => handleDrill(homeView)} /></ErrorBoundary>}
      {view === "subregion" && selectedSR && <ErrorBoundary label="SubregionView"><SubregionView sr={selectedSR} onDrill={handleDrill} onBack={() => handleDrill('ld', selectedLD)} onJumpToRoute={handleJumpToRoute} focusRoute={routeFocus} onRouteFocusConsumed={() => setRouteFocus(null)} /></ErrorBoundary>}
      {view === "precinct" && selectedPrecinct !== null && <ErrorBoundary label="PrecinctView"><PrecinctView precinctNum={selectedPrecinct} onDrill={handleDrill} onBack={() => handleDrill('subregion', selectedSR)} /></ErrorBoundary>}
      {view === "voters" && <ErrorBoundary label="VotersView"><VotersView userRole={user?.role} onBack={() => handleDrill(homeView)} onJumpToRoute={handleJumpToRoute} /></ErrorBoundary>}
      {view === "routes" && <ErrorBoundary label="RoutesView"><RoutesView onDrill={handleDrill} onBack={() => handleDrill(homeView)} /></ErrorBoundary>}
      {view === "routes-list" && <ErrorBoundary label="RoutesListView"><RoutesListView onBack={() => handleDrill(homeView)} /></ErrorBoundary>}
      {view === "activity" && <ErrorBoundary label="ActivityView"><ActivityView userRole={user?.role} /></ErrorBoundary>}
      {view === "coverage" && <ErrorBoundary label="CoverageView"><CoverageView onBack={() => handleDrill(homeView)} /></ErrorBoundary>}
      {view === "power-map" && <ErrorBoundary label="PowerMapView"><PowerMapView onBack={() => handleDrill(homeView)} /></ErrorBoundary>}
      {view === "scripts" && <ErrorBoundary label="ScriptsView"><ScriptsView onBack={() => handleDrill(homeView)} /></ErrorBoundary>}
      {view === "heatmap" && <ErrorBoundary label="TurnoutHeatmapView"><TurnoutHeatmapView onDrill={handleDrill} onBack={() => handleDrill(homeView)} /></ErrorBoundary>}
      {view === "dropoff" && <ErrorBoundary label="DropOffView"><DropOffView onDrill={handleDrill} onBack={() => handleDrill(homeView)} /></ErrorBoundary>}
      {view === "canvass" && <ErrorBoundary label="CanvassView"><CanvassView onBack={() => handleDrill(homeView)} /></ErrorBoundary>}
      {view === "canvass-review" && <ErrorBoundary label="CanvassReviewView"><CanvassReviewView onBack={() => handleDrill(homeView)} /></ErrorBoundary>}
      {view === "campaign-issues" && <ErrorBoundary label="CampaignIssuesView"><CampaignIssuesView onBack={() => handleDrill(homeView)} /></ErrorBoundary>}
      {view === "talking-points" && <ErrorBoundary label="CampaignTalkingPointsView"><CampaignTalkingPointsView onBack={() => handleDrill(homeView)} /></ErrorBoundary>}
      {view === "optimizer" && <ErrorBoundary label="OptimizerView"><OptimizerView onBack={() => handleDrill(homeView)} /></ErrorBoundary>}
      {view === "engagement" && <ErrorBoundary label="EngagementHeatmapView"><EngagementHeatmapView onDrill={handleDrill} onBack={() => handleDrill(homeView)} /></ErrorBoundary>}
      {view === "r-lean" && <ErrorBoundary label="RLeanView"><RLeanView onBack={() => handleDrill(homeView)} /></ErrorBoundary>}
      {view === "turnout-accuracy" && <ErrorBoundary label="TurnoutAccuracyView"><TurnoutAccuracyView onBack={() => handleDrill(homeView)} /></ErrorBoundary>}
      {view === "feedback" && <ErrorBoundary label="FeedbackView"><FeedbackView userRole={user?.role} /></ErrorBoundary>}
      {view === "data-changes" && <ErrorBoundary label="DataChangesView"><DataChangesView userRole={user?.role} onBack={() => setView(homeView)} onJumpToRoute={handleJumpToRoute} /></ErrorBoundary>}
      {view === "leads" && <ErrorBoundary label="LeadsView"><LeadsView userRole={user?.role} /></ErrorBoundary>}
      {view === "my-staff" && <ErrorBoundary label="MyVolunteersView"><MyVolunteersView userRole={user?.role} /></ErrorBoundary>}
      {view === "my-responses" && <ErrorBoundary label="MyResponsesView"><MyResponsesView userRole={user?.role} /></ErrorBoundary>}
      {view === "my-profile" && <ErrorBoundary label="MyProfileView"><MyProfileView userRole={user?.role} /></ErrorBoundary>}
      {view === "mail-builder" && <ErrorBoundary label="MailListBuilderView"><MailListBuilderView userRole={user?.role} /></ErrorBoundary>}
      {view === "compliance" && <ErrorBoundary label="ComplianceView"><ComplianceView userRole={user?.role} /></ErrorBoundary>}
      {view === "channel-planner" && <ErrorBoundary label="ChannelPlannerView"><ChannelPlannerView userRole={user?.role} /></ErrorBoundary>}
      {view === "enrichment-reconcile" && <ErrorBoundary label="EnrichmentReconcileView"><EnrichmentReconcileView userRole={user?.role} onBack={() => setView(homeView)} /></ErrorBoundary>}
      {view === "users" && <ErrorBoundary label="UsersAdminView"><UsersAdminView /></ErrorBoundary>}
      {view === "candidates" && <ErrorBoundary label="CandidatesAdminView"><CandidatesAdminView /></ErrorBoundary>}
      {view === "campaign-setup" && <ErrorBoundary label="CampaignSetupView"><CampaignSetupView /></ErrorBoundary>}
      {view === "settings" && <ErrorBoundary label="Settings"><SettingsView /></ErrorBoundary>}
      {view === "glossary" && <ErrorBoundary label="GlossaryView"><GlossaryView onBack={() => handleDrill(homeView)} initialTermId={glossaryTermId} /></ErrorBoundary>}
      {/* SMS messaging (Track B, 2026-05-17) */}
      {view === "messenger-queue" && (
        <ErrorBoundary label="MessengerQueueView">
          <MessengerQueueView
            userRole={user?.role}
            currentUser={user ? { id: user.id, full_name: user.full_name } : undefined}
            onBack={() => handleDrill(homeView)}
          />
        </ErrorBoundary>
      )}
      {view === "campaigns" && (
        <ErrorBoundary label="CampaignsView">
          <CampaignsView
            userRole={user?.role}
            onNavigate={(v, p) => handleDrill(v as any, p as any)}
          />
        </ErrorBoundary>
      )}
      {view === "campaign-composer" && (
        <ErrorBoundary label="CampaignComposerView">
          <CampaignComposerView
            userRole={user?.role}
            currentUser={user ? { id: user.id, full_name: user.full_name ?? undefined } : undefined}
            onNavigate={(v, p) => handleDrill(v as any, p as any)}
            onCancel={() => handleDrill('campaigns')}
          />
        </ErrorBoundary>
      )}
      {view === "campaign-detail" && campaignDetailId != null && (
        <ErrorBoundary label="CampaignDetailView">
          <CampaignDetailView
            campaignId={campaignDetailId}
            userRole={user?.role}
            onBack={() => handleDrill('campaigns')}
          />
        </ErrorBoundary>
      )}
      {view === "messaging-numbers" && (
        <ErrorBoundary label="MessagingNumbersView">
          <MessagingNumbersView
            userRole={user?.role}
            onBack={() => handleDrill(homeView)}
          />
        </ErrorBoundary>
      )}
    </AppShell>
  );
}
